import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';

interface WorkingHourEntry {
  open: string;
  close: string;
  closed: boolean;
}

interface Doctor {
  name: string;
  title: string;
  branch?: string;
  specialty: string;
  specialtyCategory?: string;
  experience: number;
  image: string;
  bio?: string;
  cvFileUrl?: string;
}

interface Institution {
  id: string;
  name: string;
  description: string;
  city: string;
  district: string;
  address: string;
  email: string;
  website?: string;
  workingHours: string;
  services: string[];
  departments?: string[];
  doctors?: Doctor[];
  certificates?: string[];
  tech_devices?: string[];
  foundedYear?: number;
  coverImage?: string;
  workingHoursData?: Record<string, WorkingHourEntry>;
}

interface Props {
  institution: Institution;
}

interface VideoLinkItem {
  id: string;
  title: string;
  url: string;
}

interface BlogLinkItem {
  id: string;
  title: string;
  url: string;
}

/* -------------------------------------------------
   PhotoLightbox
   ------------------------------------------------- */
function PhotoLightbox({
  photos,
  startIndex,
  onClose,
}: {
  photos: string[];
  startIndex: number;
  onClose: () => void;
}) {
  const [current, setCurrent] = useState(startIndex);

  const goPrev = () =>
    setCurrent((p) => (p > 0 ? p - 1 : photos.length - 1));
  const goNext = () =>
    setCurrent((p) => (p < photos.length - 1 ? p + 1 : 0));

  if (!photos.length) return null;

  return (
    <div
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div
        className="relative max-w-4xl w-full max-h-[85vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 w-9 h-9 flex items-center justify-center text-white/80 hover:text-white bg-white/10 rounded-full transition-colors cursor-pointer z-10"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        {photos.length > 1 && (
          <button
            onClick={goPrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/40 hover:bg-black/60 text-white rounded-full transition-colors cursor-pointer z-10"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
        )}

        <img
          src={photos[current]}
          alt=""
          className="max-w-full max-h-[80vh] object-contain rounded-xl"
        />

        {photos.length > 1 && (
          <button
            onClick={goNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/40 hover:bg-black/60 text-white rounded-full transition-colors cursor-pointer z-10"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
        )}

        <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-white/60 text-xs">
          {current + 1} / {photos.length}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------
   PhotoGrid
   ------------------------------------------------- */
function PhotoGrid({
  photos,
  maxShow,
  label,
  onOpen,
}: {
  photos: string[];
  maxShow: number;
  label: string;
  onOpen: (idx: number) => void;
}) {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const displayPhotos = showAll ? photos : photos.slice(0, maxShow);
  const hasMore = photos.length > maxShow;

  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
        <i className="ri-gallery-line text-rose-400 mr-1.5"></i>
        {label} ({photos.length})
      </h4>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {displayPhotos.map((photo, idx) => (
          <button
            key={idx}
            onClick={() => onOpen(idx)}
            className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden group cursor-pointer"
          >
            <img
              src={photo}
              alt={`${label} ${idx + 1}`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
              <i className="ri-zoom-in-line text-white text-lg opacity-0 group-hover:opacity-100 transition-opacity"></i>
            </div>
          </button>
        ))}
      </div>
      {hasMore && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="mt-3 text-xs text-rose-600 hover:text-rose-700 font-medium cursor-pointer flex items-center"
        >
          {showAll
            ? <><i className="ri-arrow-up-s-line mr-0.5"></i>{t('tab_show_less')}</>
            : <><i className="ri-arrow-down-s-line mr-0.5"></i>{t('tab_show_more', { count: photos.length - maxShow })}</>
          }
        </button>
      )}
    </div>
  );
}

/* -------------------------------------------------
   CvViewerModal
   ------------------------------------------------- */
function CvViewerModal({
  url,
  doctorName,
  onClose,
}: {
  url: string;
  doctorName: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fileType, setFileType] = useState<'pdf' | 'office' | 'unknown'>('unknown');

  // Dosyayı fetch ile çekip blob URL oluştur
  // Bu yöntem Content-Disposition: attachment sorununu tamamen çözer
  useEffect(() => {
    let objectUrl: string | null = null;

    const fetchAndCreateBlob = async () => {
      setLoading(true);
      setLoadError(false);
      setBlobUrl(null);

      try {
        const response = await fetch(url, { mode: 'cors' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const contentType = response.headers.get('content-type') || '';
        const lowerUrl = url.toLowerCase().split('?')[0];

        let detectedType: 'pdf' | 'office' | 'unknown' = 'unknown';
        let mimeType = contentType;

        if (lowerUrl.endsWith('.pdf') || contentType.includes('pdf')) {
          detectedType = 'pdf';
          mimeType = 'application/pdf';
        } else if (
          lowerUrl.endsWith('.docx') ||
          lowerUrl.endsWith('.doc') ||
          contentType.includes('word') ||
          contentType.includes('officedocument')
        ) {
          detectedType = 'office';
          mimeType = lowerUrl.endsWith('.docx')
            ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            : 'application/msword';
        } else if (lowerUrl.endsWith('.xlsx') || lowerUrl.endsWith('.xls')) {
          detectedType = 'office';
        } else if (lowerUrl.endsWith('.pptx') || lowerUrl.endsWith('.ppt')) {
          detectedType = 'office';
        }

        setFileType(detectedType);

        const blob = await response.blob();
        // MIME type'ı doğru set et
        const typedBlob = mimeType && mimeType !== contentType
          ? new Blob([blob], { type: mimeType })
          : blob;

        objectUrl = URL.createObjectURL(typedBlob);
        setBlobUrl(objectUrl);
      } catch (err) {
        console.error('CV fetch error:', err);
        setLoadError(true);
      }
      setLoading(false);
    };

    fetchAndCreateBlob();

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  const handleOpenNewTab = () => {
    // Yeni sekmede de blob URL ile aç — indirme tetiklenmez
    if (blobUrl) {
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center px-4 py-6"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col overflow-hidden"
        style={{ height: '85vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50 flex-shrink-0">
          <div className="flex items-center space-x-2">
            <div className="w-7 h-7 flex items-center justify-center bg-rose-100 rounded-lg">
              <i className="ri-file-user-line text-rose-600 text-sm"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">{doctorName}</p>
              <p className="text-xs text-gray-400">{t('tab_cv_file')}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleOpenNewTab}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-600 hover:bg-gray-200 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-external-link-line"></i>
              {t('tab_open_new_tab')}
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>

        {/* Viewer */}
        <div className="flex-1 overflow-hidden bg-gray-100 relative">

          {/* Yükleniyor */}
          {loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 z-10">
              <div className="animate-spin w-8 h-8 border-[3px] border-rose-500 border-t-transparent rounded-full mb-3"></div>
              <p className="text-sm text-gray-500">{t('tab_cv_loading')}</p>
              <p className="text-xs text-gray-400 mt-1">{t('tab_cv_please_wait')}</p>
            </div>
          )}

          {/* Hata durumu */}
          {loadError && !loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 z-10 px-6 text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-rose-100 rounded-full mb-4">
                <i className="ri-file-warning-line text-2xl text-rose-500"></i>
              </div>
              <p className="text-sm font-semibold text-gray-700 mb-1">{t('tab_cv_error')}</p>
              <p className="text-xs text-gray-400 mb-4">{t('tab_cv_error_desc')}</p>
              <button
                onClick={handleOpenNewTab}
                className="flex items-center gap-1.5 px-4 py-2 bg-rose-600 text-white rounded-lg text-xs font-medium hover:bg-rose-700 transition-colors cursor-pointer"
              >
                <i className="ri-external-link-line"></i>
                {t('tab_cv_view_new_tab')}
              </button>
            </div>
          )}

          {/* PDF: iframe ile görüntüle — blob URL olduğu için Content-Disposition sorunu yok */}
          {!loading && blobUrl && fileType === 'pdf' && (
            <iframe
              src={blobUrl}
              className="w-full h-full border-0"
              title={`${doctorName} CV`}
            />
          )}

          {/* Office dosyaları (docx, xlsx vb): Office Online Viewer ile görüntüle */}
          {!loading && blobUrl && fileType === 'office' && (
            <iframe
              src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`}
              className="w-full h-full border-0"
              title={`${doctorName} CV`}
            />
          )}

          {/* Bilinmeyen format: iframe ile dene */}
          {!loading && blobUrl && fileType === 'unknown' && (
            <iframe
              src={blobUrl}
              className="w-full h-full border-0"
              title={`${doctorName} CV`}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------
   DoctorBioModal
   ------------------------------------------------- */
function DoctorBioModal({
  doctor,
  onClose,
}: {
  doctor: Doctor | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [showCvViewer, setShowCvViewer] = useState(false);

  if (!doctor) return null;

  const getCvFileIcon = (url: string) => {
    const lower = url.toLowerCase();
    if (lower.includes('.pdf')) return { icon: 'ri-file-pdf-2-line', color: 'text-red-500', bg: 'bg-red-50 border-red-100', label: 'PDF' };
    if (lower.includes('.doc')) return { icon: 'ri-file-word-2-line', color: 'text-sky-600', bg: 'bg-sky-50 border-sky-100', label: 'Word' };
    return { icon: 'ri-file-text-line', color: 'text-gray-500', bg: 'bg-gray-50 border-gray-200', label: 'File' };
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4 py-6"
        onClick={onClose}
      >
        <div
          className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center space-x-4 p-5 border-b border-gray-100 bg-gradient-to-r from-rose-50 to-pink-50 flex-shrink-0">
            <div className="w-14 h-14 bg-rose-100 rounded-xl flex-shrink-0 overflow-hidden flex items-center justify-center">
              {doctor.image ? (
                <img
                  src={doctor.image}
                  alt={doctor.name}
                  className="w-full h-full object-cover object-top"
                />
              ) : (
                <i className="ri-user-star-line text-2xl text-rose-400"></i>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-bold text-gray-900">{doctor.name}</h3>
              <p className="text-sm text-rose-600 font-medium mt-0.5">{doctor.title}</p>
              {doctor.specialty && (
                <p className="text-xs text-gray-500 mt-0.5">{doctor.specialty}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-white rounded-full transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {/* Body */}
          <div className="overflow-y-auto p-5 flex-1 space-y-4">
            {/* CV Dosyası */}
            {doctor.cvFileUrl && (() => {
              const fileInfo = getCvFileIcon(doctor.cvFileUrl!);
              return (
                <button
                  onClick={() => setShowCvViewer(true)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl border ${fileInfo.bg} hover:opacity-80 transition-opacity cursor-pointer group text-left`}
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-white rounded-lg flex-shrink-0 shadow-sm">
                    <i className={`${fileInfo.icon} text-xl ${fileInfo.color}`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800">{t('tab_cv_file')}</p>
                    <p className={`text-xs ${fileInfo.color} font-medium`}>{t('tab_cv_format', { format: fileInfo.label })}</p>
                  </div>
                  <div className="w-8 h-8 flex items-center justify-center text-gray-400 group-hover:text-gray-600 flex-shrink-0">
                    <i className="ri-eye-line text-sm"></i>
                  </div>
                </button>
              );
            })()}

            {/* Bio metni */}
            {doctor.bio && (
              <div
                className="text-sm text-gray-600 leading-relaxed prose prose-sm max-w-none
                  [&_h3]:text-base [&_h3]:font-bold [&_h3]:text-gray-800 [&_h3]:mb-2 [&_h3]:mt-4
                  [&_h4]:text-sm [&_h4]:font-semibold [&_h4]:text-gray-700 [&_h4]:mb-1 [&_h4]:mt-3
                  [&_ul]:list-disc [&_ul]:ml-5 [&_ul]:space-y-1
                  [&_ol]:list-decimal [&_ol]:ml-5 [&_ol]:space-y-1
                  [&_strong]:font-semibold [&_strong]:text-gray-800
                  [&_p]:mb-2"
                dangerouslySetInnerHTML={{ __html: doctor.bio }}
              />
            )}

            {/* Ne bio ne de dosya varsa */}
            {!doctor.bio && !doctor.cvFileUrl && (
              <div className="text-center py-8 text-gray-400">
                <i className="ri-file-user-line text-4xl mb-2 block"></i>
                <p className="text-sm">{t('tab_no_cv')}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end px-5 py-3 border-t border-gray-100 flex-shrink-0">
            <button
              onClick={onClose}
              className="px-5 py-2 bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('tab_close')}
            </button>
          </div>
        </div>
      </div>

      {/* CV Viewer */}
      {showCvViewer && doctor.cvFileUrl && (
        <CvViewerModal
          url={doctor.cvFileUrl}
          doctorName={doctor.name}
          onClose={() => setShowCvViewer(false)}
        />
      )}
    </>
  );
}

// Admin panelindeki sekme sırası ile senkronize olmak için aynı storage key
const ADMIN_TAB_ORDER_KEY = 'institution_tab_order';

// Admin sekme id'lerini kurum detay sekme id'lerine map'le
const ADMIN_TO_DETAIL_TAB_MAP: Record<string, string> = {
  genel: 'genel',
  branslar: 'branslar',
  sertifikalar: 'sertifikalar',
  doktorlar: 'doktorlar',
  medya: 'medya',
  videoblog: 'videoblog',
  ivf: 'ivf',
  onizleme: '',
  kullanici: '',
};

const ALL_DETAIL_TABS_DEF_KEYS = [
  { id: 'genel', labelKey: 'tab_general', icon: 'ri-information-line' },
  { id: 'branslar', labelKey: 'tab_branches', icon: 'ri-hospital-line' },
  { id: 'sertifikalar', labelKey: 'tab_certificates', icon: 'ri-award-line' },
  { id: 'doktorlar', labelKey: 'tab_doctors', icon: 'ri-user-star-line' },
  { id: 'medya', labelKey: 'tab_media', icon: 'ri-image-line' },
  { id: 'videoblog', labelKey: 'tab_videoblog', icon: 'ri-video-line' },
  { id: 'ivf', labelKey: 'tab_ivf', icon: 'ri-award-line' },
];

function getSortedDetailTabs() {
  try {
    const saved = localStorage.getItem(ADMIN_TAB_ORDER_KEY);
    if (saved) {
      const adminOrder: string[] = JSON.parse(saved);
      const detailOrder: string[] = [];
      const seen = new Set<string>();
      for (const adminId of adminOrder) {
        const detailId = ADMIN_TO_DETAIL_TAB_MAP[adminId];
        if (detailId && !seen.has(detailId)) { seen.add(detailId); detailOrder.push(detailId); }
      }
      const ordered = detailOrder.map(id => ALL_DETAIL_TABS_DEF_KEYS.find(t => t.id === id)).filter(Boolean) as typeof ALL_DETAIL_TABS_DEF_KEYS;
      const missing = ALL_DETAIL_TABS_DEF_KEYS.filter(t => !detailOrder.includes(t.id));
      return [...ordered, ...missing];
    }
  } catch { /* ignore */ }
  return ALL_DETAIL_TABS_DEF_KEYS;
}

/* -------------------------------------------------
   Main component
   ------------------------------------------------- */
export default function DetailTabs({ institution }: Props) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('genel');
  const [selectedDoctorBio, setSelectedDoctorBio] = useState<Doctor | null>(null);
  const [lightboxPhotos, setLightboxPhotos] = useState<string[]>([]);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const [doctorFilter, setDoctorFilter] = useState<string>('all');
  const [sortedTabs, setSortedTabs] = useState(getSortedDetailTabs);

  const [mediaGalleryPhotos, setMediaGalleryPhotos] = useState<string[]>([]);
  const [ivfGalleryPhotos, setIvfGalleryPhotos] = useState<string[]>([]);
  const [ivfDescription, setIvfDescription] = useState('');
  const [videoLinks, setVideoLinks] = useState<VideoLinkItem[]>([]);
  const [blogLinks, setBlogLinks] = useState<BlogLinkItem[]>([]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === ADMIN_TAB_ORDER_KEY) setSortedTabs(getSortedDetailTabs());
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    const loadGalleryAndIvf = async () => {
      if (!institution.id) return;
      try {
        const { data: instData } = await supabase
          .from('institutions')
          .select('ivf_description, video_link, blog_link, video_links, blog_links')
          .eq('id', institution.id)
          .maybeSingle();

        if (instData) {
          setIvfDescription(instData.ivf_description || '');
          const rawVideoLinks = (instData as any).video_links;
          if (rawVideoLinks && Array.isArray(rawVideoLinks) && rawVideoLinks.length > 0) setVideoLinks(rawVideoLinks);
          else if (instData.video_link) setVideoLinks([{ id: 'v1', title: 'Video', url: instData.video_link }]);
          else setVideoLinks([]);

          const rawBlogLinks = (instData as any).blog_links;
          if (rawBlogLinks && Array.isArray(rawBlogLinks) && rawBlogLinks.length > 0) setBlogLinks(rawBlogLinks);
          else if (instData.blog_link) setBlogLinks([{ id: 'b1', title: 'Blog', url: instData.blog_link }]);
          else setBlogLinks([]);
        }

        const { data: galleryData } = await supabase
          .from('institution_gallery').select('*').eq('institution_id', institution.id).order('sort_order', { ascending: true });

        if (galleryData) {
          const mediaPhotos: string[] = [];
          const ivfPhotos: string[] = [];
          galleryData.forEach((item: { file_url: string; caption: string | null }) => {
            if (item.caption === 'ivf') ivfPhotos.push(item.file_url);
            else mediaPhotos.push(item.file_url);
          });
          setMediaGalleryPhotos(mediaPhotos);
          setIvfGalleryPhotos(ivfPhotos);
        }
      } catch (err) {
        console.error('Failed to load gallery/ivf data:', err);
      }
    };
    loadGalleryAndIvf();
  }, [institution.id]);

  const hasDoctors = (institution.doctors?.length ?? 0) > 0;
  const hasIvfSection = ivfGalleryPhotos.length > 0;
  const hasMediaSection = !!(institution.coverImage || mediaGalleryPhotos.length > 0);
  const hasVideoBlogSection = videoLinks.length > 0 || blogLinks.length > 0;
  const hasBranches = (institution.departments?.length ?? 0) > 0;
  const hasCertificates = (institution.certificates?.length ?? 0) > 0;
  const hasTechDevices = (institution.tech_devices?.length ?? 0) > 0;
  const hasCertSection = hasCertificates || hasTechDevices;

  const visibleTabs = sortedTabs.filter((tab) => {
    if (tab.id === 'branslar' && !hasBranches) return false;
    if (tab.id === 'sertifikalar' && !hasCertSection) return false;
    if (tab.id === 'doktorlar' && !hasDoctors) return false;
    if (tab.id === 'ivf' && !hasIvfSection) return false;
    if (tab.id === 'videoblog' && !hasVideoBlogSection) return false;
    return true;
  });

  const openLightbox = (photos: string[], index: number) => {
    setLightboxPhotos(photos);
    setLightboxIdx(index);
  };

  const hasWorkingHours = institution.workingHoursData && Object.keys(institution.workingHoursData).length > 0;

  const workingDayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const workingDayNames: Record<string, string> = {
    monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday',
    thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday',
  };

  return (
    <div className="bg-white rounded-2xl shadow-md overflow-hidden">
      {/* Tab Navigation */}
      <div className="border-b border-gray-100 px-4 sm:px-6">
        <div className="flex flex-wrap gap-1 py-3">
          {visibleTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-rose-50 text-rose-700 shadow-sm'
                  : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
              }`}
            >
              <i className={`${tab.icon} text-sm`}></i>
              <span>{t(tab.labelKey)}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-5 sm:p-7">
        {/* ===== GENERAL INFO ===== */}
        {activeTab === 'genel' && (
          <div className="space-y-7">
            {institution.description ? (
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-information-line text-rose-500 mr-2"></i>{t('tab_about')}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">{institution.description}</p>
              </div>
            ) : (
              <div className="text-center py-6 bg-gray-50 rounded-xl">
                <i className="ri-file-text-line text-3xl text-gray-300 mb-2 block"></i>
                <p className="text-sm text-gray-400">{t('tab_no_description')}</p>
              </div>
            )}

            {institution.foundedYear && (
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-calendar-line text-rose-500 mr-2"></i>{t('tab_founded_year')}
                </h3>
                <div className="inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-rose-50 to-pink-50 rounded-xl border border-rose-100">
                  <div className="w-9 h-9 flex items-center justify-center bg-rose-100 rounded-lg mr-3">
                    <i className="ri-building-2-line text-rose-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-gray-900">{institution.foundedYear}</p>
                    <p className="text-[11px] text-gray-500">{t('tab_experience_years', { count: new Date().getFullYear() - institution.foundedYear })}</p>
                  </div>
                </div>
              </div>
            )}

            {hasWorkingHours && (
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-time-line text-rose-500 mr-2"></i>{t('tab_working_hours')}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {workingDayOrder.map((day) => {
                    const entry = institution.workingHoursData?.[day];
                    if (!entry) return null;
                    return (
                      <div
                        key={day}
                        className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg"
                      >
                        <span className="text-xs font-medium text-gray-700">{workingDayNames[day] || day}</span>
                        {entry.closed ? (
                          <span className="text-xs text-red-400 font-medium">
                            {t('tab_closed')}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-600">
                            {entry.open} - {entry.close}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {(institution.address || institution.city) && (
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-map-pin-2-line text-rose-500 mr-2"></i>{t('tab_location')}
                </h3>
                <div className="rounded-xl overflow-hidden border border-gray-100" style={{ height: '300px' }}>
                  <iframe
                    title={`${institution.name} Location`}
                    width="100%" height="100%" style={{ border: 0 }}
                    loading="lazy" allowFullScreen referrerPolicy="no-referrer-when-downgrade"
                    src={`https://www.google.com/maps?q=${encodeURIComponent([institution.address, institution.district, institution.city].filter(Boolean).join(', '))}&output=embed`}
                  />
                </div>
                {(institution.address || institution.city) && (
                  <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                    <i className="ri-map-pin-line text-rose-400"></i>
                    {[institution.address, institution.district, institution.city].filter(Boolean).join(', ')}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* ===== CERTIFICATES ===== */}
        {activeTab === 'sertifikalar' && hasCertSection && (
          <div>
            <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center">
              <i className="ri-award-line text-rose-500 mr-2"></i>{t('tab_cert_title')}
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-teal-50/60 to-emerald-50/40 rounded-2xl border border-teal-100 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-lg">
                    <i className="ri-award-fill text-teal-600 text-sm"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-gray-900">{t('tab_cert_section')}</h4>
                    <p className="text-[11px] text-teal-600">{t('tab_cert_count', { count: institution.certificates?.length ?? 0 })}</p>
                  </div>
                </div>
                {hasCertificates ? (
                  <div className="space-y-2">
                    {institution.certificates!.map(cert => (
                      <div key={cert} className="flex items-center gap-3 px-3 py-2.5 bg-white rounded-xl border border-teal-100">
                        <div className="w-7 h-7 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
                          <i className="ri-award-fill text-teal-600 text-xs"></i>
                        </div>
                        <span className="text-xs font-medium text-gray-800 leading-tight">{cert}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <i className="ri-award-line text-3xl text-teal-200 mb-2 block"></i>
                    <p className="text-xs text-gray-400">{t('tab_no_cert')}</p>
                  </div>
                )}
              </div>

              <div className="bg-gradient-to-br from-rose-50/60 to-pink-50/40 rounded-2xl border border-rose-100 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-rose-100 rounded-lg">
                    <i className="ri-microscope-line text-rose-500 text-sm"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-gray-900">{t('tab_device_section')}</h4>
                    <p className="text-[11px] text-rose-500">{t('tab_device_count', { count: institution.tech_devices?.length ?? 0 })}</p>
                  </div>
                </div>
                {hasTechDevices ? (
                  <div className="space-y-2">
                    {institution.tech_devices!.map(device => (
                      <div key={device} className="flex items-center gap-3 px-3 py-2.5 bg-white rounded-xl border border-rose-100">
                        <div className="w-7 h-7 flex items-center justify-center bg-rose-100 rounded-lg flex-shrink-0">
                          <i className="ri-settings-3-line text-rose-500 text-xs"></i>
                        </div>
                        <span className="text-xs font-medium text-gray-800 leading-tight">{device}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <i className="ri-microscope-line text-3xl text-rose-200 mb-2 block"></i>
                    <p className="text-xs text-gray-400">{t('tab_no_device')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ===== BRANCHES ===== */}
        {activeTab === 'branslar' && hasBranches && (
          <div className="space-y-4">
            <h3 className="text-base font-bold text-gray-900 flex items-center">
              <i className="ri-hospital-line text-rose-500 mr-2"></i>{t('tab_branches_title')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {institution.departments!.map((branch) => (
                <div key={branch} className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-rose-50 to-pink-50 rounded-xl border border-rose-100">
                  <div className="w-8 h-8 flex items-center justify-center bg-rose-100 rounded-lg flex-shrink-0">
                    <i className="ri-stethoscope-line text-rose-500 text-sm"></i>
                  </div>
                  <span className="text-sm font-medium text-gray-800">{branch}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ===== DOCTORS ===== */}
        {activeTab === 'doktorlar' && hasDoctors && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
              <h3 className="text-base font-bold text-gray-900 flex items-center">
                <i className="ri-user-star-line text-rose-500 mr-2"></i>{t('tab_doctors_title')}
              </h3>
              <span className="text-xs text-gray-400">{t('tab_doctor_count_label', { count: institution.doctors!.length })}</span>
            </div>

            {(() => {
              const presentBranches = Array.from(new Set(institution.doctors!.map(d => d.branch || d.title || '').filter(Boolean)));
              const allBranches = Array.from(new Set([...presentBranches, ...(institution.departments || [])]));
              const filteredDoctors = doctorFilter === 'all'
                ? institution.doctors!
                : institution.doctors!.filter(d => (d.branch || d.title || '') === doctorFilter);

              return (
                <>
                  {allBranches.length >= 2 && (
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="relative">
                        <i className="ri-stethoscope-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none"></i>
                        <select
                          value={doctorFilter}
                          onChange={(e) => setDoctorFilter(e.target.value)}
                          className="pl-8 pr-8 py-2 border border-gray-200 rounded-lg text-xs font-medium text-gray-700 bg-white outline-none focus:border-rose-400 cursor-pointer appearance-none"
                        >
                          <option value="all">{t('tab_all_branches', { count: institution.doctors!.length })}</option>
                          {allBranches.map(branch => {
                            const count = institution.doctors!.filter(d => (d.branch || d.title || '') === branch).length;
                            return (
                              <option key={branch} value={branch}>
                                {branch} {count > 0 ? `(${count})` : t('tab_branch_registered')}
                              </option>
                            );
                          })}
                        </select>
                        <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none"></i>
                      </div>
                      {doctorFilter !== 'all' && (
                        <button onClick={() => setDoctorFilter('all')} className="flex items-center gap-1 px-2.5 py-1.5 bg-rose-50 text-rose-600 rounded-lg text-xs font-medium hover:bg-rose-100 transition-colors cursor-pointer whitespace-nowrap">
                          <i className="ri-close-line text-xs"></i>{t('tab_filter_clear')}
                        </button>
                      )}
                      {doctorFilter !== 'all' && (
                        <span className="text-xs text-gray-500 bg-gray-100 px-2.5 py-1.5 rounded-lg">
                          {t('tab_showing_doctors', { count: filteredDoctors.length })}
                        </span>
                      )}
                    </div>
                  )}

                  {filteredDoctors.length === 0 ? (
                    <div className="text-center py-10 bg-gray-50 rounded-xl">
                      <i className="ri-user-search-line text-3xl text-gray-300 mb-2 block"></i>
                      <p className="text-sm text-gray-400">{t('tab_no_doctor_branch')}</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {filteredDoctors.map((doc, idx) => {
                        const docKey = `${doc.name}-${idx}`;
                        const hasCvContent = !!(doc.bio || doc.cvFileUrl);
                        return (
                          <div key={docKey} className="bg-gradient-to-br from-gray-50 to-rose-50/30 rounded-xl border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
                            <div className="flex items-start space-x-3 p-4">
                              <div className="w-16 h-16 bg-rose-100 rounded-xl flex-shrink-0 overflow-hidden flex items-center justify-center">
                                {doc.image ? <img src={doc.image} alt={doc.name} className="w-full h-full object-cover object-top" /> : <i className="ri-user-star-line text-2xl text-rose-400"></i>}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-bold text-gray-900">{doc.name}</h4>
                                <p className="text-xs text-rose-600 font-medium mt-0.5">{doc.title}</p>
                                {doc.specialty && <p className="text-xs text-gray-500 mt-0.5">{doc.specialty}</p>}
                              </div>
                            </div>
                            {hasCvContent && (
                              <div className="px-4 pb-3 flex items-center gap-3">
                                <button onClick={() => setSelectedDoctorBio(doc)} className="text-[11px] text-rose-600 hover:text-rose-700 font-medium cursor-pointer flex items-center">
                                  <i className="ri-file-user-line mr-1"></i>
                                  {t('tab_view_cv')}
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* ===== MEDIA & GALLERY ===== */}
        {activeTab === 'medya' && (
          <div className="space-y-6">
            <h3 className="text-base font-bold text-gray-900 flex items-center">
              <i className="ri-image-line text-rose-500 mr-2"></i>{t('tab_media_title')}
            </h3>
            {institution.coverImage && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                  <i className="ri-image-2-line text-rose-400 mr-1.5"></i>{t('tab_main_photo')}
                </h4>
                <div className="w-full bg-gray-50 rounded-xl overflow-hidden border border-gray-100 flex items-center justify-center" style={{ minHeight: '320px', maxHeight: '480px' }}>
                  <img src={institution.coverImage} alt="Main photo" className="w-full h-auto object-contain rounded-xl" style={{ maxHeight: '480px' }} />
                </div>
              </div>
            )}
            {mediaGalleryPhotos.length > 0 && (
              <PhotoGrid
                photos={mediaGalleryPhotos}
                maxShow={10}
                label={t('tab_gallery_photos')}
                onOpen={(idx) => openLightbox(mediaGalleryPhotos, idx)}
              />
            )}
            {!institution.coverImage && mediaGalleryPhotos.length === 0 && (
              <div className="text-center py-12 bg-gray-50 rounded-xl">
                <i className="ri-image-add-line text-5xl text-gray-300 mb-3 block"></i>
                <p className="text-sm text-gray-400">{t('tab_no_photo')}</p>
              </div>
            )}
            {lightboxIdx !== null && lightboxPhotos.length > 0 && (
              <PhotoLightbox
                photos={lightboxPhotos}
                startIndex={lightboxIdx}
                onClose={() => {
                  setLightboxIdx(null);
                  setLightboxPhotos([]);
                }}
              />
            )}
          </div>
        )}

        {/* ===== VIDEO & BLOGS ===== */}
        {activeTab === 'videoblog' && (
          <div className="space-y-6">
            <h3 className="text-base font-bold text-gray-900 flex items-center">
              <i className="ri-video-line text-rose-500 mr-2"></i>{t('tab_videoblog_title')}
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {videoLinks.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center bg-red-100 rounded-lg">
                      <i className="ri-play-circle-fill text-red-500 text-sm"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-gray-900">{t('tab_videos')}</h4>
                      <p className="text-[11px] text-red-500">{t('tab_video_count', { count: videoLinks.length })}</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {videoLinks.map((v) => {
                      let ytId: string | null = null;
                      try {
                        const u = new URL(v.url);
                        if (u.hostname.includes('youtube.com')) ytId = u.searchParams.get('v');
                        else if (u.hostname === 'youtu.be') ytId = u.pathname.slice(1).split('?')[0];
                      } catch { /* ignore */ }
                      let vimeoId: string | null = null;
                      try {
                        const u = new URL(v.url);
                        if (u.hostname.includes('vimeo.com')) { const m = u.pathname.match(/\/(\d+)/); vimeoId = m ? m[1] : null; }
                      } catch { /* ignore */ }
                      return (
                        <div key={v.id} className="rounded-2xl overflow-hidden border border-gray-100 bg-white shadow-sm">
                          {ytId ? (
                            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                              <iframe
                                src={`https://www.youtube.com/embed/${ytId}`}
                                title={v.title || 'YouTube Video'}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                className="absolute inset-0 w-full h-full border-0"
                              />
                            </div>
                          ) : vimeoId ? (
                            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                              <iframe
                                src={`https://player.vimeo.com/video/${vimeoId}`}
                                title={v.title || 'Vimeo Video'}
                                allow="autoplay; fullscreen; picture-in-picture"
                                allowFullScreen
                                className="absolute inset-0 w-full h-full border-0"
                              />
                            </div>
                          ) : (
                            <div className="flex items-center justify-center h-40 bg-gray-50">
                              <div className="text-center">
                                <i className="ri-video-line text-4xl text-gray-300 mb-2 block"></i>
                                <p className="text-xs text-gray-400">{t('tab_no_preview')}</p>
                              </div>
                            </div>
                          )}
                          <div className="px-4 py-3 flex items-center justify-between gap-2 bg-white">
                            <div className="flex-1 min-w-0">
                              {v.title && <p className="text-sm font-semibold text-gray-800 truncate">{v.title}</p>}
                              <p className="text-[11px] text-gray-400 truncate">{v.url}</p>
                            </div>
                            <a
                              href={v.url}
                              target="_blank"
                              rel="nofollow noopener noreferrer"
                              className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg flex-shrink-0 cursor-pointer transition-colors"
                            >
                              <i className="ri-external-link-line text-sm"></i>
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {blogLinks.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-lg">
                      <i className="ri-article-line text-teal-600 text-sm"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-gray-900">{t('tab_blogs')}</h4>
                      <p className="text-[11px] text-teal-600">{t('tab_blog_count', { count: blogLinks.length })}</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {blogLinks.map((b) => {
                      let domain = '';
                      try { domain = new URL(b.url).hostname.replace('www.', ''); } catch { /* ignore */ }
                      return (
                        <a
                          key={b.id}
                          href={b.url}
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                          className="flex items-center gap-4 px-4 py-4 bg-gradient-to-r from-teal-50/60 to-emerald-50/40 border border-teal-100 rounded-xl hover:border-teal-300 hover:shadow-sm transition-all cursor-pointer group"
                        >
                          <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-xl flex-shrink-0">
                            <i className="ri-article-line text-teal-600 text-xl"></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            {b.title && <p className="text-sm font-semibold text-gray-800 truncate mb-0.5">{b.title}</p>}
                            <p className="text-xs text-teal-600 truncate">{domain || b.url}</p>
                          </div>
                          <i className="ri-external-link-line text-teal-400 group-hover:text-teal-600 flex-shrink-0 text-lg"></i>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {videoLinks.length === 0 && blogLinks.length === 0 && (
              <div className="text-center py-12 bg-gray-50 rounded-xl">
                <i className="ri-video-line text-5xl text-gray-300 mb-3 block"></i>
                <p className="text-sm text-gray-400">{t('tab_no_video_blog')}</p>
              </div>
            )}
          </div>
        )}

        {/* ===== HEALTH TOURISM CERTIFICATE ===== */}
        {activeTab === 'ivf' && (
          <div className="space-y-6">
            <h3 className="text-base font-bold text-gray-900 flex items-center">
              <i className="ri-award-line text-rose-500 mr-2"></i>{t('tab_ivf_title')}
            </h3>
            {ivfGalleryPhotos.length > 0 ? (
              <PhotoGrid
                photos={ivfGalleryPhotos}
                maxShow={8}
                label={t('tab_doc_photos')}
                onOpen={(idx) => openLightbox(ivfGalleryPhotos, idx)}
              />
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-xl">
                <i className="ri-award-line text-5xl text-gray-300 mb-3 block"></i>
                <p className="text-sm text-gray-400">{t('tab_no_ivf')}</p>
              </div>
            )}
            {lightboxIdx !== null && lightboxPhotos.length > 0 && (
              <PhotoLightbox
                photos={lightboxPhotos}
                startIndex={lightboxIdx}
                onClose={() => {
                  setLightboxIdx(null);
                  setLightboxPhotos([]);
                }}
              />
            )}
          </div>
        )}
      </div>

      {selectedDoctorBio && (
        <DoctorBioModal
          doctor={selectedDoctorBio}
          onClose={() => setSelectedDoctorBio(null)}
        />
      )}
    </div>
  );
}
